import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { FieldArrayPath, useFieldArray, useFormState, useWatch } from 'react-hook-form';

import FormattedInput from 'components/FormInputs/FormattedInput.tsx';
import SelectInput from 'components/FormInputs/SelectInput';
import TextInput from 'components/FormInputs/TextInput.tsx';

import { PhoneTypes } from 'pages/Main/PackageListing/components/RegistrationForm/utils/formConstants';
import { RegistrationFormOutputValues } from 'pages/Main/PackageListing/components/RegistrationForm/utils/validationSchema';

interface PhoneFieldArrayProps {
  name: FieldArrayPath<RegistrationFormOutputValues>;
  numRequired?: number;
}

function PhoneFieldArray({ name, numRequired = 1 }: PhoneFieldArrayProps) {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });
  const fieldArray = useFieldArray({ name: name });
  const phoneValues = useWatch({ name: name });
  const { errors } = useFormState<RegistrationFormOutputValues>({
    name: name,
  });

  return (
    <Stack gap={4}>
      {fieldArray.fields.map((_field, index) => (
        <Grid key={index} container rowSpacing={2} columnSpacing={isMd ? 4 : 2}>
          <Grid size={12}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 'bold',
                textDecoration: 'underline',
              }}
            >
              Phone Number {index > 0 ? index + 1 : undefined}
            </Typography>
            <Typography color="error" fontWeight="bold">
              {errors[name]?.[index]?.root?.message}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <SelectInput
              name={`${name}.${index}.type`}
              label="Type"
              options={PhoneTypes}
              sx={{
                minWidth: '100px',
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <FormattedInput name={`${name}.${index}.phone`} label="Phone Number" format="(###) ###-####" mask="_" />
          </Grid>

          {phoneValues?.[index]?.type === 'work' && (
            <Grid size={{ xs: 12, md: 3 }}>
              <TextInput
                name={`${name}.${index}.extension`}
                label="Extension"
                InputProps={{ startAdornment: <InputAdornment position="start">Ext.</InputAdornment> }}
              />
            </Grid>
          )}

          {fieldArray.fields.length > numRequired && (
            <Grid
              size={{ xs: 12, md: phoneValues?.[index]?.type === 'work' ? 2 : 5 }}
              alignSelf="start"
              mt={isMd ? 4.5 : 0}
            >
              <Button
                variant="contained"
                size="small"
                color="error"
                fullWidth={!isMd}
                onClick={() => {
                  console.log(`Deleting index: ${index}`);
                  fieldArray.remove(index);
                }}
              >
                Remove Phone {index + 1}
              </Button>
            </Grid>
          )}
        </Grid>
      ))}

      {fieldArray.fields.length === 0 && (
        <Typography
          variant="h5"
          sx={{
            fontWeight: 'bold',
            textDecoration: 'underline',
          }}
        >
          No Phone Numbers
        </Typography>
      )}

      <Button
        variant="contained"
        size="small"
        fullWidth
        onClick={() => {
          fieldArray.append({
            priority: fieldArray.fields.length + 1,
            type: undefined,
            phone: '',
            extension: '',
          });
        }}
      >
        {fieldArray.fields.length > 0 ? 'Add Another Phone Number' : 'Add Phone Number'}
      </Button>
    </Stack>
  );
}

export default PhoneFieldArray;
